import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { GetDays, MONTH_NAME } from '@components/DatePicker/DatePicker';
import {
  ClearDateButton,
  HoverableDayCell,
} from '@components/DatePicker/HoverableDayCell';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import theme, { getScaledSize, getCommonScaledDimensions } from '@themes/theme';
import { beginningOfToday } from '@utils/formatters/date';

type DateRangePickerWebV3Props = {
  start?: Date;
  end?: Date;
  confirmButtonText?: string;
  isRangeSelector?: boolean;
  onSelect: (start: Date | undefined, end: Date | undefined) => void;
};

const CELL_SIZE = 32;
const CELL_RADIUS = CELL_SIZE / 2;

// Helper: check if two dates are the same calendar day
const isSameDay = (d1?: Date, d2?: Date) =>
  !!(d1 && d2 && dayjs(d1).isSame(d2, 'day'));

type DayCellProps = {
  displayNumber: number;
  isPrevMonth: boolean;
  isNextMonth: boolean;
  isSelectedCircle: boolean;
  isToday: boolean;
  showFullGrey: boolean;
  showHalfRightGrey: boolean;
  showHalfLeftGrey: boolean;
  hasBoth: boolean;
  onPress: () => void;
};

// TA-10950 #7/#26: grey01 hover on every selectable date.
const DayCell: React.FC<DayCellProps> = ({
  displayNumber,
  isPrevMonth,
  isNextMonth,
  isSelectedCircle,
  isToday,
  showFullGrey,
  showHalfRightGrey,
  showHalfLeftGrey,
  hasBoth,
  onPress,
}) => (
  <HoverableDayCell
    hoverDisabled={isSelectedCircle}
    onPress={onPress}
    cursor='pointer'
    accessibilityLabel={displayNumber > 0 ? `${displayNumber}` : undefined}
    radius={CELL_RADIUS}
    width={CELL_SIZE}
    height={CELL_SIZE}>
    <Box
      alignItems='center'
      justifyContent='center'
      style={styles.dayCellWrapper}>
      {(showFullGrey || showHalfRightGrey || showHalfLeftGrey) && (
        <Box
          style={[
            styles.rangeBackgroundBase,
            showFullGrey && styles.rangeBackgroundFull,
            hasBoth && showHalfRightGrey && styles.rangeBackgroundRightHalf,
            hasBoth && showHalfLeftGrey && styles.rangeBackgroundLeftHalf,
          ]}
        />
      )}

      {isSelectedCircle && <Box style={styles.selectedCircle} />}

      {!isSelectedCircle && isToday && <Box style={styles.todayOutline} />}

      <Text
        variant='metadata'
        style={[
          styles.dayLabel,
          (isPrevMonth || isNextMonth) && styles.dayLabelOutsideMonth,
          displayNumber === 0 && styles.dayLabelEmpty,
          isSelectedCircle && styles.dayLabelSelected,
        ]}>
        {displayNumber > 0 ? displayNumber : ' '}
      </Text>
    </Box>
  </HoverableDayCell>
);

export const DateRangePickerWebV3: React.FC<DateRangePickerWebV3Props> = ({
  start,
  end,
  onSelect,
  confirmButtonText,
  isRangeSelector = false,
}) => {
  const date = beginningOfToday();
  const { t } = useTranslation('format');
  const [year, setYear] = useState(date.getFullYear());
  const [month, setMonth] = useState(date.getMonth());

  // Keep current month grid and days-in-month in state
  const [weeks, setWeeks] = useState<any[]>([]);
  const [currentMonthDay, setCurrentMonthDay] = useState<number[]>([]);
  const [daysInMonth, setDaysInMonth] = useState<number>(
    GetDays(date.getFullYear(), date.getMonth())
  );

  const [startDate, setStartDate] = useState(start);
  const [untilDate, setUntilDate] = useState(end);

  // Rebuild weeks when selection changes (range background depends on it)
  useEffect(() => {
    const _weeks = getWeek(currentMonthDay, daysInMonth);
    setWeeks(_weeks);
  }, [startDate, untilDate, currentMonthDay, daysInMonth]);

  // Rebuild month grid when (year, month) changes
  useEffect(() => {
    GetMonthData(year, month);
  }, [year, month]);

  const GetMonthData = (year: number, month: number) => {
    const daysCurrent = GetDays(year, month);
    setDaysInMonth(daysCurrent);

    const currentMonthDays: number[] = [];

    // Compute previous month year & month
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonth = month === 0 ? 11 : month - 1;
    const daysPrev = GetDays(prevYear, prevMonth);

    const firstDay = new Date(year, month, 1);
    const week = firstDay.getDay(); // 0..6

    // -------------------------------
    // 1) Leading days: last days of previous month
    //    We store them as negative numbers: -actualDay
    // -------------------------------
    for (let i = 0; i < week; i++) {
      const dayNumber = daysPrev - week + 1 + i; // e.g. 29,30
      currentMonthDays.push(-dayNumber);
    }

    // -------------------------------
    // 2) Actual current month days: 1..daysCurrent (positive)
    // -------------------------------
    for (let i = 1; i <= daysCurrent; i++) {
      currentMonthDays.push(i);
    }

    // -------------------------------
    // 3) Trailing days: first days of next month
    //    We store them as > daysCurrent (daysCurrent + n)
    //    and ensure there are always 6 rows (6 * 7 = 42 cells)
    // -------------------------------
    let nextDay = 1;
    while (currentMonthDays.length < 42) {
      currentMonthDays.push(daysCurrent + nextDay);
      nextDay++;
    }

    setCurrentMonthDay(currentMonthDays);
    const _weeks = getWeek(currentMonthDays, daysCurrent);
    setWeeks(_weeks);
  };

  const onSelectDay = (day: number, xMonth: number, xYear: number) => {
    // day + xMonth + xYear
    const dateSelected = new Date(xYear, xMonth, day);

    // Case 1: nothing selected yet
    if (!startDate && !untilDate) {
      setStartDate(dateSelected);
      return;
    }

    // Case 2: have start but no end
    if (startDate && !untilDate) {
      if (isSameDay(dateSelected, startDate)) {
        // Tap same day again => clear
        setStartDate(undefined);
        return;
      }

      if (dayjs(dateSelected).isBefore(startDate, 'day')) {
        // New date before start => move start
        setStartDate(dateSelected);
      } else {
        // New date after start => set as end
        setUntilDate(dateSelected);
      }
      return;
    }

    // Case 3: both start & end exist => restart from this date
    setStartDate(dateSelected);
    setUntilDate(undefined);
  };

  const monthPre = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const monthNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // Build week rows with 6x7 cells
  // Build week rows with 6x7 cells
  const getWeek = (days: number[], daysInCurrent: number) => {
    const _weeks: JSX.Element[] = [];
    let week: JSX.Element[] = [];

    const hasBoth =
      !!startDate && !!untilDate && dayjs(startDate).isBefore(untilDate, 'day');
    const hasOnlyStart = !!startDate && !untilDate;
    const hasOnlyEnd = !!untilDate && !startDate;

    for (let i = 1; i <= days.length; i++) {
      const raw = days[i - 1];

      const isPrevMonth = raw < 0;
      const isNextMonth = raw > daysInCurrent;
      const isCurrentMonthDay = !isPrevMonth && !isNextMonth && raw > 0;

      let xMonth = month;
      let xYear = year;

      if (isPrevMonth) {
        if (month === 0) {
          xMonth = 11;
          xYear = year - 1;
        } else {
          xMonth = month - 1;
        }
      } else if (isNextMonth) {
        if (month === 11) {
          xMonth = 0;
          xYear = year + 1;
        } else {
          xMonth = month + 1;
        }
      }

      // Display number:
      //  prev month: abs(raw)
      //  current month: raw
      //  next month: raw - daysInCurrent
      let displayNumber = 0;
      if (isPrevMonth) {
        displayNumber = Math.abs(raw);
      } else if (isNextMonth) {
        displayNumber = raw - daysInCurrent;
      } else if (isCurrentMonthDay) {
        displayNumber = raw;
      }

      const dayDate =
        displayNumber > 0 ? new Date(xYear, xMonth, displayNumber) : undefined;

      const isStart = dayDate && isSameDay(dayDate, startDate);
      const isEnd = dayDate && isSameDay(dayDate, untilDate);
      const isToday =
        dayDate && dayjs(dayDate).isSame(beginningOfToday(), 'day');

      const inRange =
        hasBoth &&
        !!dayDate &&
        dayjs(dayDate).isAfter(startDate!, 'day') &&
        dayjs(dayDate).isBefore(untilDate!, 'day');

      const showFullGrey = hasBoth && inRange;
      const showHalfRightGrey = (hasBoth || hasOnlyStart) && !!isStart;
      const showHalfLeftGrey = (hasBoth || hasOnlyEnd) && !!isEnd;

      const isSelectedCircle = !!(isStart || isEnd);

      week.push(
        <DayCell
          key={year + month + 'touch' + i}
          displayNumber={displayNumber}
          isPrevMonth={isPrevMonth}
          isNextMonth={isNextMonth}
          isSelectedCircle={isSelectedCircle}
          isToday={!!isToday}
          showFullGrey={showFullGrey}
          showHalfRightGrey={showHalfRightGrey}
          showHalfLeftGrey={showHalfLeftGrey}
          hasBoth={hasBoth}
          onPress={() => {
            if (displayNumber > 0) {
              onSelectDay(displayNumber, xMonth, xYear);
            }
          }}
        />
      );

      if (i % 7 === 0) {
        _weeks.push(
          <Box
            px='m'
            backgroundColor='white'
            key={year + month + 'week' + i}
            flex={1}
            flexDirection='row'
            justifyContent='space-between'>
            {week}
          </Box>
        );
        week = [];
      }
    }

    return _weeks;
  };

  let counter = 0;

  const disableMode = () => {
    return false;
  };
  return (
    <Box
      flex={Platform.OS === 'web' ? 1 : 0.7}
      accessibilityLabel='Date range picker'>
      <Box mt='m' alignItems='center'>
        <Text variant='webSmall' color='textPrimary' numberOfLines={1}>
          {t('models:projects.taskList.selectDates')}
        </Text>
      </Box>

      {/* Top: start / end input display */}
      <Box
        py='xs'
        borderTopColor='grey02'
        borderTopWidth={1}
        borderBottomColor='grey02'
        borderBottomWidth={1}
        px='m'
        marginTop='m'
        justifyContent='flex-start'
        flexDirection='row'
        alignItems='flex-start'>
        <Box flex={1} mr='m'>
          <Text variant='labelSmall' color='textPrimary' numberOfLines={1}>
            {t('models:projects.taskList.startDate')}
          </Text>
          <Box
            flex={1}
            mt='xs'
            height={48}
            backgroundColor='white'
            borderColor='grey02'
            borderWidth={1}
            borderRadius='xs'
            flexDirection='row'
            alignItems='center'
            accessibilityLabel={t('models:projects.taskList.startDate')}>
            <Text
              variant='webBodySecondary'
              color={startDate ? 'textPrimary' : 'grey04'}
              marginLeft='s'
              flex={1}>
              {startDate
                ? dayjs(startDate).format('MM / DD / YYYY')
                : 'MM/DD/YYYY'}
            </Text>
            {/*<TextInput*/}
            {/*  editable={false}*/}
            {/*  style={[*/}
            {/*    {*/}
            {/*      marginLeft: theme.spacing.xs,*/}
            {/*      flex: 1,*/}
            {/*      ...Platform.select({ web: { outlineStyle: 'none' } }),*/}
            {/*    },*/}
            {/*    theme.textVariants.webBodySecondary as any,*/}
            {/*    { height: getCommonScaledDimensions().inputHeight },*/}
            {/*  ]}*/}
            {/*  placeholderTextColor={theme.colors.grey04}*/}
            {/*  placeholder='MM/DD/YYYY'*/}
            {/*  value={startDate ? dayjs(startDate).format('MM / DD / YYYY') : ''}*/}
            {/*  autoCapitalize='none'*/}
            {/*/>*/}
            {/*<TouchableOpacity*/}
            {/*  onPress={() => {*/}
            {/*    setStartDate(undefined);*/}
            {/*  }}>*/}
            <Box
              mr='s'
              width={24}
              height={48}
              alignItems='center'
              justifyContent='center'>
              {isRangeSelector && startDate && (
                <Icon
                  name='Close'
                  variant='xxxs'
                  color='textPrimary'
                  onPress={() => {
                    setStartDate(undefined);
                  }}
                />
              )}
            </Box>
            {/*</TouchableOpacity>*/}
          </Box>
        </Box>

        <Box flex={1}>
          <Text variant='labelSmall' color='textPrimary' numberOfLines={1}>
            {t('models:projects.taskList.endDate')}
          </Text>
          <Box
            flex={1}
            mt='xs'
            backgroundColor='white'
            borderColor='grey02'
            style={{ borderWidth: 1 }}
            borderRadius='xs'
            flexDirection='row'
            alignItems='center'
            accessibilityLabel={t('models:projects.taskList.endDate')}>
            <Text
              variant='webBodySecondary'
              color={untilDate ? 'textPrimary' : 'grey04'}
              marginLeft='s'
              flex={1}>
              {untilDate
                ? dayjs(untilDate).format('MM / DD / YYYY')
                : 'MM/DD/YYYY'}
            </Text>
            {/*<TextInput*/}
            {/*  editable={false}*/}
            {/*  style={[*/}
            {/*    styles.input,*/}
            {/*    theme.textVariants.webBodySecondary as any,*/}
            {/*    { height: getCommonScaledDimensions().inputHeight, flex: 1 },*/}
            {/*  ]}*/}
            {/*  placeholderTextColor={theme.colors.grey04}*/}
            {/*  placeholder='MM/DD/YYYY'*/}
            {/*  value={untilDate ? dayjs(untilDate).format('MM / DD / YYYY') : ''}*/}
            {/*  autoCapitalize='none'*/}
            {/*/>*/}
            <Box
              mr='s'
              width={24}
              height={48}
              alignItems='center'
              justifyContent='center'>
              {isRangeSelector && untilDate && (
                <Icon
                  name='Close'
                  variant='xxxs'
                  color='textPrimary'
                  onPress={() => {
                    setUntilDate(undefined);
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Month / year header */}
      <Box
        px='m'
        flexDirection='row'
        flexWrap='wrap'
        justifyContent='space-between'
        marginBottom='s'
        alignItems='flex-start'>
        <Box
          alignItems='center'
          flexDirection='row'
          flex={1}
          height={getCommonScaledDimensions().inputHeight}>
          <WebIcon
            onPress={monthPre}
            name='ChevronLeft'
            variant='l'
            color='textPrimary'
            accessibilityLabel='Previous month'
          />

          <Box alignItems='center' flex={1}>
            <Text textAlign='center' variant='bodyTaskName'>
              {MONTH_NAME[month]} {year}
            </Text>
          </Box>

          <WebIcon
            onPress={monthNext}
            name='ChevronRight'
            variant='l'
            color='textPrimary'
            accessibilityLabel='Next month'
          />
        </Box>
      </Box>

      {/* Weekday header row */}
      <Box
        flexDirection='row'
        height={getCommonScaledDimensions().calendarHeader}
        marginBottom='xxs'
        px='m'
        justifyContent='space-between'>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((i) => {
          counter++;
          return (
            <Text key={counter} style={styles.weekdayLabel}>
              {i}
            </Text>
          );
        })}
      </Box>

      {/* Calendar grid (always 6 rows) */}
      {weeks}

      {/* Footer buttons */}
      <Box
        borderTopWidth={1}
        borderTopColor='grey02'
        flexDirection='row'
        justifyContent='flex-end'
        alignItems='center'
        pt='s'
        mb='s'
        height={getCommonScaledDimensions().calendarFooter}
        left={0}
        right={0}
        px='m'>
        <Box flex={1} />
        <ClearDateButton
          hoverDisabled={!startDate && !untilDate}
          onPress={() => {
            setStartDate(undefined);
            setUntilDate(undefined);
          }}
          cursor={startDate || untilDate ? 'pointer' : 'not-allowed'}
          width={getScaledSize(125)}
          height={getScaledSize(48)}
        />

        <TouchableOpacity
          accessibilityLabel={t('shared:confirm')}
          disabled={disableMode()}
          onPress={() => {
            if (!disableMode()) {
              onSelect(startDate, untilDate);
            }
          }}>
          <Box
            ml='xs'
            height={getScaledSize(48)}
            width={getScaledSize(107)}
            flexDirection='row'
            borderWidth={1}
            style={{ backgroundColor: disableMode() ? '#303742' : 'black' }}
            borderRadius='xs'
            alignItems='center'
            justifyContent='center'>
            <Text variant='labelSmall' color='white'>
              {confirmButtonText || t('shared:confirm')}
            </Text>
          </Box>
        </TouchableOpacity>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  input: {
    marginLeft: theme.spacing.xs,
    flex: 1,
    ...Platform.select({ web: { outlineStyle: 'none' } }),
  },

  // Fixed-size wrapper for each day cell, so text and shapes are always centered
  dayCellWrapper: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    marginVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },

  // Base style for any grey range background
  rangeBackgroundBase: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: theme.colors.grey02,
  },

  // Full grey fill for days between start and end
  rangeBackgroundFull: {
    left: -8,
    width: 48,
    borderRadius: 0,
  },

  // Right-half grey (for start day)
  rangeBackgroundRightHalf: {
    left: CELL_RADIUS,
    width: 24,
  },

  // Left-half grey (for end day)
  rangeBackgroundLeftHalf: {
    right: CELL_RADIUS,
    width: 24,
  },

  // Black circle for start / end: exactly 32x32
  selectedCircle: {
    position: 'absolute',
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: CELL_RADIUS,
    backgroundColor: theme.colors.black,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Outline circle for "today" when not selected
  todayOutline: {
    position: 'absolute',
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: CELL_RADIUS,
    borderWidth: 1,
    borderColor: theme.colors.grey05,
  },

  // Day label text, always centered
  dayLabel: {
    textAlign: 'center',
    textAlignVertical: 'center' as any,
    color: theme.colors.textSecondary,
    zIndex: 2,
  },

  // Days outside the current month should be grey04
  dayLabelOutsideMonth: {
    color: theme.colors.grey04,
  },

  // Empty label fallback (should not normally be used now)
  dayLabelEmpty: {
    color: 'transparent',
  },

  // Text color when inside selected circle
  dayLabelSelected: {
    color: theme.colors.white,
    fontWeight: '600',
  },

  // Weekday header labels; width matches cell width
  weekdayLabel: {
    width: CELL_SIZE,
    textAlign: 'center',
    marginTop: getScaledSize(8),
    ...theme.textVariants.webBodySecondary,
  },
});
